import { type CopySettings } from '@/features/copySettings';
import { copyCanonicalNote } from '@/helpers/copyHelpers/copyCanonical';
import {
  isFormattingOff,
  transformHtmlWithSettings,
  writeHtmlAndMarkdownToClipboard,
} from '@/helpers/copyHelpers/copyHelpers-v2';
import { resolvePlainCopyMarkdown } from '@/helpers/copyHelpers/plainTextClipboard';
import { simpleHtmlToMarkdown } from '@/helpers/htmlToMarkdown';
import { reformatHtml } from '@/helpers/markdownToHtml';
import { serializeSectionPresentationMarkdown } from '@heidi/editor/core';
// Type-only: loads @tiptap/markdown's Editor augmentation (editor.markdown).
import type {} from '@tiptap/markdown';
import { type Node } from '@tiptap/pm/model';
import { generateHTML, type Editor, type JSONContent } from '@tiptap/react';
import { atomWithStorage } from 'jotai/utils';
import { stripEmptySectionHeadingPresentationHtml } from './section-markers';

export type SectionCopyFormat = 'lists' | 'plain';

export type SectionCopySettings = {
  includeHeading: boolean;
  format: SectionCopyFormat;
};

// Section H1 styling is off by default: a copied section pastes as plain
// body text, matching how notes looked before sections. Users opt back into
// the heading via the "Headings" toggle in the copy-settings submenu.
export const DEFAULT_SECTION_COPY_SETTINGS: SectionCopySettings = {
  includeHeading: false,
  format: 'lists',
};

/**
 * Persisted per-device preference for section copy settings.
 * Each click in the submenu both applies the setting and copies, mirroring
 * the version-controller "copy" preference UX.
 */
export const sectionCopySettingsAtom = atomWithStorage<SectionCopySettings>(
  'heidi.sectionCopySettings',
  DEFAULT_SECTION_COPY_SETTINGS
);

const HEADING_PATTERN = /<h1\b[^>]*>([\s\S]*?)<\/h1>/gi;
// Quote-aware, like `createSectionElementRegex`: `data-section-title` carries
// the raw title, and HTML attribute serialization escapes `"` but not `>`, so
// a "Plan > Follow-up" heading would cut a `[^>]*` match short.
const SECTION_OPEN_PATTERN = /<section\b(?:[^>"']|"[^"]*"|'[^']*')*>/gi;
const SECTION_CLOSE_PATTERN = /<\/section>/gi;
const LIST_MARKER_PATTERN = /^- /gm;

/** `<h1>` handling: preserve for rich HTML, rewrite to `<p># …</p>` for markdown, or omit. */
export type SectionCopyHeadingMode = 'preserve' | 'markdown' | 'omit';

// Doc-wrapped because `generateHTML` renders the *content* of the node it is
// given. The `<section>` wrapper is what tells `simpleHtmlToMarkdown` the `<h1>`
// is a section title; without it the raw tag reaches the clipboard.
const generateSectionHtml = (sectionNode: Node, editor: Editor): string =>
  stripEmptySectionHeadingPresentationHtml(
    generateHTML(
      { type: 'doc', content: [sectionNode.toJSON()] } as JSONContent,
      editor.extensionManager.extensions
    )
  );

const applyHeadingMode = (html: string, heading: SectionCopyHeadingMode): string => {
  const withHeading =
    heading === 'omit'
      ? html.replace(HEADING_PATTERN, '')
      : heading === 'markdown'
        ? html.replace(HEADING_PATTERN, '<p># $1</p>')
        : html;
  return withHeading.replace(SECTION_OPEN_PATTERN, '').replace(SECTION_CLOSE_PATTERN, '');
};

/** Generates section HTML for clipboard use. Section wrapper tags are always stripped. */
export const buildSectionCopyHtml = (
  sectionNode: Node,
  editor: Editor,
  { heading }: { heading: SectionCopyHeadingMode }
): string => applyHeadingMode(generateSectionHtml(sectionNode, editor), heading);

/**
 * Copies section content to the clipboard using the given settings.
 * - `format: 'lists'` writes both HTML and markdown with list markers preserved
 *   (reuses `writeHtmlAndMarkdownToClipboard` from copyHelpers-v2).
 * - `format: 'plain'` writes text/plain only. List dashes are stripped here;
 *   `resolvePlainCopyMarkdown` then either pre-strips annotations (rollout
 *   off) or hands the funnel raw markdown so its stripper can protect
 *   snake_case literals (rollout on) — the same double-strip guard as
 *   `copyPlainTextV2`.
 */
export const copySectionWithSettings = async (
  sectionNode: Node,
  editor: Editor,
  settings: SectionCopySettings
): Promise<void> => {
  // Serialize once, apply two cheap regex transforms — rich-target HTML keeps
  // `<h1>`, markdown-target HTML rewrites it for `simpleHtmlToMarkdown`.
  const baseHtml = generateSectionHtml(sectionNode, editor);
  const richHtml = applyHeadingMode(
    baseHtml,
    settings.includeHeading ? 'preserve' : 'omit'
  );
  const markdownHtml = applyHeadingMode(
    baseHtml,
    settings.includeHeading ? 'markdown' : 'omit'
  );
  const markdown = simpleHtmlToMarkdown({ html: markdownHtml, isCopy: true });

  if (settings.format === 'plain') {
    const plainMarkdown = resolvePlainCopyMarkdown(markdown).replace(
      LIST_MARKER_PATTERN,
      ''
    );
    await writeHtmlAndMarkdownToClipboard({ markdown: plainMarkdown });
    return;
  }

  const reformattedHtml = reformatHtml(richHtml);
  await writeHtmlAndMarkdownToClipboard({ html: reformattedHtml, markdown });
};

/**
 * Which twin's note-level producer a section copy should mirror. Declared by
 * the node view mounting the button rather than sniffed from the editor: the
 * twins serialize through different pipelines, and inferring it would let a
 * schema change silently re-route the clipboard.
 */
export type SectionCopyRoute = 'markdown-native' | 'vmarkdown';

type SectionNoteSettingsCopier = (
  sectionNode: Node,
  editor: Editor,
  settings: CopySettings
) => Promise<void>;

/**
 * markdown-native route. Serializes the section through the editor's own
 * markdown manager and applies the toggles with the canonical transform — the
 * same producer the note-level Copy button uses for these notes, so the two
 * agree by construction. Never touches the legacy regex pipeline, which would
 * leave semantic `<ul>/<li>` unconverted in the plain-text flavor.
 */
export const copyMarkdownNativeSectionWithNoteSettings: SectionNoteSettingsCopier =
  async (sectionNode, editor, settings) => {
    const markdownManager = editor.markdown;
    if (!markdownManager) {
      // Wiring error, not a user-input case: this copier is only ever handed to
      // the markdown-native node view, whose editor always registers one.
      throw new Error('section copy: no markdown manager on this editor');
    }

    await copyCanonicalNote({
      markdownContent: serializeSectionPresentationMarkdown(
        [sectionNode.toJSON() as JSONContent],
        (node) => markdownManager.serialize(node)
      ),
      settings,
    });
  };

/**
 * vMarkdown route. Used when `release-scribe-improve-copy-actions` is on so
 * section copy reuses the same preference — and the same transform engine — as
 * that editor's note-level Copy button, mirroring `copyWithSettingsV2`.
 */
export const copySectionWithNoteSettings: SectionNoteSettingsCopier = async (
  sectionNode,
  editor,
  settings
) => {
  // The `<section>` wrapper is handed over intact, exactly as the note-level
  // path passes `editor.getHTML()` — for a single-section note the two inputs
  // are byte-identical, and so are the clipboard payloads.
  const { html, markdown } = transformHtmlWithSettings({
    editorHtml: generateSectionHtml(sectionNode, editor),
    settings,
  });

  // Same flavor decision as `copyWithSettingsV2`: bullets alone are
  // plain-text markers and don't warrant a text/html payload.
  if (isFormattingOff(settings)) {
    await writeHtmlAndMarkdownToClipboard({ markdown });
    return;
  }

  await writeHtmlAndMarkdownToClipboard({ html: reformatHtml(html), markdown });
};
